import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, SparkIcon, sparkToast } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { data_from_server_post } from '@/shared/http/client';
import { TaskType } from '@/shared/domain/taskTypes';
import { taskUrlForUsecase } from '@/features/tasks/utils/navigation';
import { deriveInitialTaskAction } from '@/features/tasks/utils/deriveInitialTaskAction';
import {
    saveTaskDraft,
    removeTaskDraft,
    isPastWizardSetup,
} from '@/features/tasks/utils/taskDrafts';
import { stampWizardStep } from '@/features/tasks/api/wizardStepApi';
import { DEFAULT_CLIENT_ID } from '@/config/client';
import { sanitizeTaskName } from '../HomePage/projectNameUtils';
import WizardStepper, {
    AGENT_CONFIG_STEP,
    normalizeWizardStep,
    wizardStepIndex,
    wizardStepsFor,
} from './WizardStepper';
import BusinessGoalsStep from './steps/BusinessGoalsStep';
import AgentConfigStep from './steps/AgentConfigStep';
import DataCollectionStep from './steps/DataCollectionStep';
import ModelSetupStep from './steps/ModelSetupStep';
import ReviewTrainingPlanModal from './reviewPlan/ReviewTrainingPlanModal';
import WizardDataEditorModal from './reviewPlan/WizardDataEditorModal';
import AutoTrainProgressBanner from '@/features/trainings/components/CurrentTraining/AutoTrainProgressBanner';
import { consumeAutoTrainNotifyModalShown } from '@/features/trainings/utils/autoTrain';
import {
    DEFAULT_PILLAR_ORDER,
    constraintsFromBackend,
    constraintsToPayload,
    constraintRowError,
} from './businessGoals/constraintOptions';
import { fallbackLabelFromStored, fallbackPayloadFromSelect } from './businessGoals/fallbackLabel';
import {
    AUTONOMY_LEVEL_NO_TOOL_CALLING,
    AUTONOMY_LEVEL_ONE_PREDETERMINED_TOOL_CALL,
    isAgenticAutonomyLevel,
    normalizeAutonomyLevel,
} from './businessGoals/autonomyLevels';
import { useAuth } from '@/features/auth/AuthContext';
import {
    DEMO_TOUR_QUERY_PARAM,
    DEMO_TOUR_QUERY_VALUE,
    WALKTHROUGH_COMPLETED_STATE_KEY,
    isDemoTourSearch,
    useWalkthroughContextValue,
} from '@/features/walkthrough';
import DemoScope from '@/features/walkthrough/demo/DemoScope';
import DemoTourRunner from '@/features/walkthrough/demo/DemoTourRunner';
import { useDemoTour } from '@/features/walkthrough/demo/useDemoTour';
import { DEMO_USECASE_ID, buildDemoUsecase } from '@/features/walkthrough/demo/demoFixtures';
import { DEMO_CLASSIFICATION_TYPE } from '@/features/walkthrough/demo/demoScript';
import {
    RegisterWizardApi,
    WIZARD_API,
    WizardApiProvider,
} from '@/features/walkthrough/demo/wizardApi';
import WizardAssistant from '@/features/assistant/components/WizardAssistant';
import {
    REOPEN_ASSISTANT_LABEL,
    WIZARD_ASSISTANT_QUERY_PARAM,
    WIZARD_ASSISTANT_QUERY_VALUE,
} from '@/features/assistant/constants';
import { agentConfigHasTools } from './agentConfig/agentConfigModel';
import './creation-wizard.css';

/**
 * Empty wizard form for a new draft. Self-hosted: no task type / autonomy
 * default — the user must pick explicitly. SaaS (`isSaas`): Classification
 * and "no tool calling" are the only enabled choices, so they are pre-selected
 * (the gated options stay visible but disabled). Resumed usecases are
 * hydrated from their stored values afterwards and are not affected.
 */
export const buildInitialForm = ({ isSaas = false } = {}) => ({
    taskName: '',
    description: '',
    // No default — the user must explicitly pick a task type.
    taskType: isSaas ? TaskType.classification : null,
    // No default — the user must explicitly pick Yes (multi) or No (single).
    classificationType: null,
    labels: [],
    // Fallback target: Best guess, No predictions, one of `labels`, or null
    // until chosen. Required on the Business Goals step once labels exist.
    fallbackLabel: null,
    // Whether the user actively picked the fallback (vs. an auto default).
    fallbackLabelUserSet: false,
    // Autonomy level 1–4 (required). Any level other than 1 makes the task
    // agentic: the wizard gains the Agent Config step and Data Collection
    // asks about trajectories.
    autonomyLevel: isSaas ? AUTONOMY_LEVEL_NO_TOOL_CALLING : null,
    // "What matters most for these labels?" — `{ kind, label }` on single-label
    // classification with exactly two labels, null otherwise. Options 2–5
    // derive `fallbackLabel`; all options seed the Train step defaults.
    labelPriority: null,
    priorityOrder: [...DEFAULT_PILLAR_ORDER],
    constraints: [],
});

/** Default project name when creating a task outside an existing project. */
const DEFAULT_PROJECT_NAME = 'Untitled-Project';

/**
 * Full-page task creation wizard (Business Goals → [Agent Config →]
 * Data Collection → Model Setup). Finish & Train opens Review Training Plan;
 * the Data Editor opens from that modal. Replaces the legacy Create Project /
 * New Task modals. Figma 9846-17245 / 9846-17681.
 *
 * Entry points:
 *   /create-task                     — "Create New Project" (project auto-named)
 *   /create-task?project=<name>      — "Add new task" inside an existing project
 *   /create-task?usecaseId=…&step=…  — resume a created task mid-flow
 *                                      (Projects draft card, table row, or
 *                                      top-navigation task switcher)
 *
 * Exiting via the top-left "← Back" (from any step) returns to the Projects
 * page; a created-but-unfinished task is kept as a draft (see taskDrafts.js).
 * Opening that draft from the Projects card or the top-navigation task
 * switcher reopens the wizard at the step the user left.
 */
const CreateTaskWizardPage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        allUsecases,
        refreshUsecases,
        setCurrentUsecaseId,
        currentSubGroup,
        evaluationResultsReady,
    } = useContext(GlobalContext);

    // Guided demo tour (`?tour=demo`, from the empty projects page): the
    // wizard runs as a pure client-side mock. The task lives in `demoUsecase`
    // below, step components render against it through `DemoScope`, and
    // nothing is created, saved, drafted or trained. Only `step` and `tour`
    // travel in the URL; a resume/project param is ignored in demo mode.
    const isDemo = isDemoTourSearch(searchParams);
    const projectParam = isDemo ? null : searchParams.get('project');
    const usecaseIdParam = isDemo ? null : searchParams.get('usecaseId');
    const stepParam = searchParams.get('step');

    const auth = useAuth();
    const isSaas = auth?.isSaas === true;

    // Spark assistant: hosted service (`IS_SAAS`) only, open on every real
    // visit to the wizard (Start Building, Create New Specific Model, resume,
    // and any other entry). Self-hosted installs have no panel and no
    // question-mark button; the demo tour is itself a guide, so it has no
    // panel either. Closing hides the panel for this visit only; the
    // question-mark button in the top bar brings it back, and the next visit
    // opens it again.
    const assistantAvailable = isSaas && !isDemo;
    const [assistantClosed, setAssistantClosed] = useState(false);
    const assistantOpen = assistantAvailable && !assistantClosed;
    // Demo: "Can one example have more than one label?" has no mark in the
    // tour, so it starts answered No (shown selected once the type is picked).
    const [form, setForm] = useState(() =>
        isDemo
            ? { ...buildInitialForm({ isSaas }), classificationType: DEMO_CLASSIFICATION_TYPE }
            : buildInitialForm({ isSaas })
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [createdUsecaseId, setCreatedUsecaseId] = useState(usecaseIdParam || null);
    // Resume (?usecaseId=) must wait for usecase hydration before Data Collection
    // mounts — otherwise the form briefly has no/wrong task type, label
    // extraction may run, then task type flips and sections appear/vanish.
    const [resumeFormReady, setResumeFormReady] = useState(() => !usecaseIdParam);

    // Data Collection step state (picked files, details forms, languages),
    // kept here because the step unmounts on Back/Next navigation.
    const dataCollectionCacheRef = useRef(null);

    // Demo task: exists from the first render (so `DemoScope` always has a
    // task to provide) but only becomes the wizard's "created" task once the
    // Business Goals step is submitted, like a real task would. The tour is
    // not persisted, so a reload or a stale `?step=` always restarts at
    // Business Goals (see the URL normalisation below).
    const [demoUsecase, setDemoUsecase] = useState(() => (isDemo ? buildDemoUsecase() : null));
    useDemoTour(isDemo);

    const createdUsecase = useMemo(() => {
        if (!createdUsecaseId) return null;
        if (isDemo) return createdUsecaseId === DEMO_USECASE_ID ? demoUsecase : null;
        return allUsecases.find((u) => u._id === createdUsecaseId);
    }, [allUsecases, createdUsecaseId, demoUsecase, isDemo]);

    // Autonomy level driving the step list. The form wins (the user may have
    // just changed it on Business Goals); the stored usecase covers resumes
    // before hydration. While a deep-linked usecase is still loading and the
    // URL asks for Agent Config, keep that step so the URL is not rewritten
    // to Data Collection before the real level is known.
    const usecaseLoading = Boolean(createdUsecaseId) && !createdUsecase;
    const autonomyLevel =
        normalizeAutonomyLevel(form.autonomyLevel) ??
        normalizeAutonomyLevel(createdUsecase?.autonomy_level) ??
        (usecaseLoading && stepParam === AGENT_CONFIG_STEP.key
            ? AUTONOMY_LEVEL_ONE_PREDETERMINED_TOOL_CALL
            : null);
    const isAgentic = isAgenticAutonomyLevel(autonomyLevel);
    const steps = useMemo(() => wizardStepsFor(autonomyLevel), [autonomyLevel]);
    const stepAfterBusinessGoals = isAgentic ? AGENT_CONFIG_STEP.key : 'data-collection';

    const activeStep = useMemo(() => {
        // Old `?step=data-editor` / `?step=train` links are Model Setup.
        const key = normalizeWizardStep(stepParam || 'business-goals');
        if (wizardStepIndex(key, steps) >= 0) return key;
        // `step=agent-config` on a non-agentic task lands on Data Collection.
        return key === AGENT_CONFIG_STEP.key ? 'data-collection' : 'business-goals';
    }, [stepParam, steps]);

    // Review Training Plan sits on Model Setup. The Data Editor opens on top
    // of it; closing the editor refreshes the occurrence counts.
    const [reviewOpen, setReviewOpen] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [overviewRefreshKey, setOverviewRefreshKey] = useState(0);

    // Steps after the first require a created usecase.
    const effectiveStep = createdUsecaseId ? activeStep : 'business-goals';
    const effectiveStepIdx = wizardStepIndex(effectiveStep, steps);

    // The card body is the page's only scroller (the page itself never
    // scrolls, see creation-wizard.css) and the same element hosts every
    // step, so a new step would otherwise open at the previous step's scroll
    // offset. Start each step at its top.
    const cardBodyRef = useRef(null);
    useEffect(() => {
        const body = cardBodyRef.current;
        if (body) body.scrollTop = 0;
    }, [effectiveStep]);

    // Guided tour (demo only): `DemoTourRunner` anchors the `create_model`
    // coach marks to this page's controls; the form is published so step
    // predicates can tell when the asked-for action is done — only once a
    // reopened draft is hydrated, so the tour is not resolved against the
    // empty form in the meantime. The real wizard shows no walkthrough at
    // all: Spark is its guide, and no coach mark is mounted on any page.
    const walkthroughPageOrder = useMemo(() => steps.map((s) => s.key), [steps]);
    useWalkthroughContextValue({ form: resumeFormReady ? form : undefined });

    // Furthest step the user has reached, so going back never locks the
    // already-visited steps in the stepper (forward navigation stays enabled).
    // Tracked by key: the index shifts when Agent Config appears/disappears.
    const [maxReachedStepKey, setMaxReachedStepKey] = useState(effectiveStep);
    useEffect(() => {
        setMaxReachedStepKey((prev) => {
            const prevIdx = wizardStepIndex(prev, steps);
            return prevIdx >= effectiveStepIdx ? prev : effectiveStep;
        });
    }, [effectiveStep, effectiveStepIdx, steps]);
    // A reached step that left the list (Agent Config after switching to a
    // non-agentic level) falls back to the current step.
    const maxReachedStep =
        wizardStepIndex(maxReachedStepKey, steps) >= 0 ? maxReachedStepKey : effectiveStep;

    // A shared or stale wizard link (?usecaseId=…&step=…) for a task that
    // already left the wizard (training started or finished) must not reopen
    // the wizard — redirect to task management instead. The tab follows
    // deriveInitialTaskAction: Evaluation when evaluation results exist, the
    // default Data Overview otherwise. Checked once when the deep-linked
    // usecase first resolves, so the in-wizard "training kicked off"
    // transition (TrainStep → finishToTaskPage) is not hijacked by a status
    // update arriving mid-flow.
    const wizardFinishedCheckRef = useRef(false);
    useEffect(() => {
        if (isDemo || wizardFinishedCheckRef.current || !createdUsecase) return;
        wizardFinishedCheckRef.current = true;
        if (!isPastWizardSetup(createdUsecase)) return;
        // Sibling objectives in the same task bucket drive the multi-objective
        // tab behavior, matching the Projects card/row entry points.
        const siblingUsecases = createdUsecase.sub_group_name
            ? allUsecases.filter(
                  (u) =>
                      u.group_name === createdUsecase.group_name &&
                      u.sub_group_name === createdUsecase.sub_group_name
              )
            : [createdUsecase];
        const action = deriveInitialTaskAction(createdUsecase, {
            isMultiObjective: siblingUsecases.length > 1,
            evaluationResultsReady,
        });
        const url = taskUrlForUsecase(createdUsecase, {
            task_id: createdUsecase._id,
            action,
        });
        if (!url) return;
        setCurrentUsecaseId(createdUsecase._id);
        navigate(url, { replace: true });
    }, [allUsecases, createdUsecase, evaluationResultsReady, isDemo, navigate, setCurrentUsecaseId]);

    // Steps 2-5 show the task name above the step title. Fall back to the
    // created usecase for deep-linked resumes (?usecaseId=&step=) where the
    // hydration effect below has not filled the form yet.
    const taskName =
        form.taskName || createdUsecase?.sub_group_name || createdUsecase?.usecase_name || '';

    // When resuming mid-flow (?usecaseId=…) the form starts empty — hydrate it
    // once from the loaded usecase so the editable task-type/labels controls in
    // later steps reflect the created task.
    const hydratedFromUsecase = useRef(false);
    useEffect(() => {
        if (!createdUsecase || hydratedFromUsecase.current) return;
        hydratedFromUsecase.current = true;
        if (form.taskName.trim()) {
            // Step 1 already filled the form (normal create flow).
            setResumeFormReady(true);
            return;
        }
        setForm((prev) => ({
            ...prev,
            taskName: createdUsecase.sub_group_name || createdUsecase.usecase_name || '',
            description: createdUsecase.usecase_goal || '',
            taskType: createdUsecase.task_type || prev.taskType,
            // Only classification has the single/multi choice; leaving it
            // null elsewhere keeps the "explicitly pick" rule for drafts
            // saved before a task type was chosen.
            classificationType:
                createdUsecase.task_type === TaskType.classification
                    ? createdUsecase.is_multilabel
                        ? 'multi'
                        : 'single'
                    : null,
            fallbackLabel: fallbackLabelFromStored({
                isClassification: createdUsecase.task_type === TaskType.classification,
                isMultilabel: Boolean(createdUsecase.is_multilabel),
                rejectionLabelName: createdUsecase.rejection_label_name,
                confidenceRejectionEnabled: createdUsecase.confidence_rejection_enabled,
            }),
            // A stored explicit label, or any single-label choice, is an
            // active choice; a multi-label default stays auto-managed.
            fallbackLabelUserSet: Boolean(
                createdUsecase.rejection_label_name ||
                    (createdUsecase.task_type === TaskType.classification &&
                        !createdUsecase.is_multilabel)
            ),
            // Server-validated: only present on qualifying two-label tasks.
            labelPriority: createdUsecase.label_priority || null,
            labels:
                createdUsecase.task_type === TaskType.NER
                    ? createdUsecase.manually_added_entities || []
                    : createdUsecase.task_type === TaskType.classification
                      ? createdUsecase.manually_added_labels || []
                      : [],
            autonomyLevel:
                normalizeAutonomyLevel(createdUsecase.autonomy_level) ?? prev.autonomyLevel,
            // Saved business-goal priorities and constraints — otherwise a
            // resumed draft shows defaults and Next overwrites the server
            // values with them.
            priorityOrder: createdUsecase.business_goal_priorities?.length
                ? [...createdUsecase.business_goal_priorities]
                : prev.priorityOrder,
            constraints: createdUsecase.constraints?.length
                ? constraintsFromBackend(createdUsecase.constraints)
                : prev.constraints,
        }));
        setResumeFormReady(true);
    }, [createdUsecase, form.taskName]);

    // Fresh create (no ?usecaseId=) is ready immediately; if the param appears
    // later (after create), keep ready so Data Collection does not unmount.
    useEffect(() => {
        if (!usecaseIdParam) setResumeFormReady(true);
    }, [usecaseIdParam]);

    // Drop Data Collection file/details cache when task type changes so a
    // Classification→Summarization (or reverse) switch cannot revive a stale
    // "share details" unchecked state or extracted-label snapshot.
    useEffect(() => {
        dataCollectionCacheRef.current = null;
    }, [form.taskType]);

    const goToStep = useCallback(
        (stepKey, usecaseId = createdUsecaseId) => {
            // Assistant `go_to_step` and any leftover navigation to the removed
            // steps open Review Training Plan on Model Setup.
            if (stepKey === 'data-editor' || stepKey === 'train') setReviewOpen(true);
            const params = { step: normalizeWizardStep(stepKey) };
            if (isDemo) {
                params[DEMO_TOUR_QUERY_PARAM] = DEMO_TOUR_QUERY_VALUE;
            } else {
                if (usecaseId) params.usecaseId = usecaseId;
                if (projectParam) params.project = projectParam;
                // Keep the Spark panel across page turns until it is closed.
                if (assistantOpen) {
                    params[WIZARD_ASSISTANT_QUERY_PARAM] = WIZARD_ASSISTANT_QUERY_VALUE;
                }
            }
            setSearchParams(params);
        },
        [assistantOpen, createdUsecaseId, isDemo, projectParam, setSearchParams]
    );

    // Closing Spark hides the panel for this visit and drops `?assistant=1`
    // from the URL. A later visit (including a reload) opens it again.
    const handleCloseAssistant = useCallback(() => {
        setAssistantClosed(true);
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete(WIZARD_ASSISTANT_QUERY_PARAM);
                return next;
            },
            { replace: true }
        );
    }, [setSearchParams]);

    const handleReopenAssistant = useCallback(() => {
        setAssistantClosed(false);
    }, []);

    // Demo mode always begins at Business Goals: a reload mid-demo (or a
    // shared `?tour=demo&step=…` link) has no task to show the later steps
    // for, so the stale `step` is dropped from the URL once on mount.
    useEffect(() => {
        if (!isDemo || !stepParam) return;
        setSearchParams({ [DEMO_TOUR_QUERY_PARAM]: DEMO_TOUR_QUERY_VALUE }, { replace: true });
        // Mount-only normalisation; later `step` changes come from goToStep.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A bookmarked Data Editor or Train step is Model Setup with the review
    // modal open. A deep link that also names a tab or the unlabeled filter
    // (the floating "Review them manually" card) opens the Data Editor on
    // top and keeps those params for DataSelection to consume. Demo mode
    // restarts at Business Goals instead (effect above).
    useEffect(() => {
        if (isDemo || !createdUsecaseId) return;
        if (stepParam !== 'data-editor' && stepParam !== 'train') return;
        setReviewOpen(true);
        if (searchParams.get('tab') || searchParams.get('labelFilter')) {
            setEditorOpen(true);
        }
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set('step', 'model-setup');
                return next;
            },
            { replace: true }
        );
    }, [isDemo, createdUsecaseId, searchParams, setSearchParams, stepParam]);

    // A created-but-unfinished task is a resumable draft: keep its current
    // step recorded so the Projects page shows a draft card that reopens the
    // wizard exactly where the user left, however they exit (Back button,
    // browser navigation, closing the tab). The same step is stamped on the
    // task server-side (fire-and-forget) for the user-journey funnel. The demo
    // task is never a draft.
    useEffect(() => {
        if (!createdUsecaseId || isDemo) return;
        saveTaskDraft(createdUsecaseId, effectiveStep);
        stampWizardStep(createdUsecaseId, effectiveStep);
    }, [createdUsecaseId, effectiveStep, isDemo]);

    // Exiting with a draft lands inside the draft's project (drilled-in
    // cards view) so the user immediately sees the draft card.
    const exitToProject = useCallback(
        (groupName) => {
            navigate(groupName ? `/?project=${encodeURIComponent(groupName)}` : '/');
        },
        [navigate]
    );

    // Guards against a double-clicked Back firing two creates/updates.
    const exitInFlightRef = useRef(false);

    /**
     * update_llm_usecase payload for the current Business Goals form. Shared
     * by the step's Next (update-in-place) and by the wizard exit, which must
     * persist edits made after resuming a draft. `onlyValidConstraints` drops
     * incomplete constraint rows (exit is best-effort; a validation failure
     * would lose the whole save).
     */
    const buildUsecaseUpdatePayload = useCallback(
        (subGroupName, { onlyValidConstraints = false } = {}) => {
            const isClassification = form.taskType === TaskType.classification;
            const isNer = form.taskType === TaskType.NER;
            const { confidence_rejection_enabled: confidenceRejectionEnabled, rejection_label_name: rejectionLabelName } =
                fallbackPayloadFromSelect(form.fallbackLabel, {
                    isClassification,
                    classificationType: form.classificationType,
                });
            return {
                usecase_id: createdUsecaseId,
                client_id: createdUsecase?.client_id || DEFAULT_CLIENT_ID,
                usecase_name: subGroupName,
                sub_group_name: subGroupName,
                usecase_goal: form.description || '',
                task_type: form.taskType,
                group_name: createdUsecase?.group_name,
                is_multilabel: isClassification && form.classificationType === 'multi',
                classify_speakers: createdUsecase?.classify_speakers || false,
                confidence_rejection_enabled: confidenceRejectionEnabled,
                rejection_label_name: rejectionLabelName,
                task_languages: createdUsecase?.task_languages?.length
                    ? createdUsecase.task_languages
                    : ['en'],
                business_goal_priorities: form.priorityOrder,
                constraints: constraintsToPayload(
                    onlyValidConstraints
                        ? form.constraints.filter((c) => !constraintRowError(c))
                        : form.constraints
                ),
                ...(isClassification && { manually_added_labels: form.labels }),
                ...(isNer && { manually_added_entities: form.labels }),
                ...(normalizeAutonomyLevel(form.autonomyLevel) !== null && {
                    autonomy_level: normalizeAutonomyLevel(form.autonomyLevel),
                }),
                // null is not a "clear" sentinel: the server keeps a stored
                // choice while the task still qualifies and drops it once it
                // stops (label count / type change), so sending the form value
                // as-is is always right.
                label_priority: isClassification ? form.labelPriority : null,
            };
        },
        [form, createdUsecase, createdUsecaseId]
    );

    // Top-left "← Back" exits the wizard to the Projects page from any step.
    // The created task (if any) stays there as a draft card. When the user
    // typed a task name on step 1 but never clicked Next (no usecase yet —
    // task type may still be unchosen), the task is created best-effort as a
    // draft so the name is not lost.
    const handleExitToProjects = useCallback(() => {
        // Demo: nothing to save — back to the projects page (the choice
        // screen is still there, the workspace was never touched).
        if (isDemo) {
            navigate('/');
            return;
        }
        if (createdUsecaseId) {
            // Business Goals edits made after resuming a draft (task type,
            // single/multi label, labels, fallback, …) only live in form
            // state until Next — persist them best-effort so reopening the
            // draft restores the exact same selections.
            const subGroupName = sanitizeTaskName(form.taskName, { trimEnd: true });
            if (effectiveStep === 'business-goals' && resumeFormReady && subGroupName) {
                if (exitInFlightRef.current) return;
                exitInFlightRef.current = true;
                const exit = () => exitToProject(createdUsecase?.group_name);
                data_from_server_post(
                    'update_llm_usecase',
                    buildUsecaseUpdatePayload(subGroupName, { onlyValidConstraints: true }),
                    (result) => {
                        if (!result?.success) {
                            sparkToast.warning(
                                result?.message || 'Could not save the draft changes'
                            );
                            exit();
                            return;
                        }
                        refreshUsecases(exit);
                    },
                    () => {
                        sparkToast.warning('Could not save the draft changes');
                        exit();
                    }
                );
                return;
            }
            exitToProject(createdUsecase?.group_name);
            return;
        }

        if (exitInFlightRef.current) return;
        const subGroupName = sanitizeTaskName(form.taskName, { trimEnd: true });
        if (!subGroupName) {
            navigate('/');
            return;
        }
        exitInFlightRef.current = true;

        const groupName = projectParam || DEFAULT_PROJECT_NAME;
        const task = {
            usecase_name: subGroupName,
            sub_group_name: subGroupName,
            usecase_goal: form.description || '',
            // May be null — the draft card then shows the "unknown" tile.
            task_type: form.taskType,
            task_languages: ['en'],
            business_goal_priorities: form.priorityOrder,
            // Only fully-filled constraint rows; incomplete ones would fail
            // server-side validation and lose the whole draft.
            constraints: constraintsToPayload(
                form.constraints.filter((c) => !constraintRowError(c))
            ),
        };
        if (form.taskType === TaskType.classification && form.labels.length) {
            task.manually_added_labels = form.labels;
            if (form.labelPriority) task.label_priority = form.labelPriority;
        }
        if (form.taskType === TaskType.NER && form.labels.length) {
            task.manually_added_entities = form.labels;
        }
        if (normalizeAutonomyLevel(form.autonomyLevel) !== null) {
            task.autonomy_level = normalizeAutonomyLevel(form.autonomyLevel);
        }

        data_from_server_post(
            'create_llm_usecase',
            { group_name: groupName, client_id: DEFAULT_CLIENT_ID, tasks: [task] },
            (result) => {
                if (!result?.success) {
                    // Best-effort: the draft could not be saved (e.g. name
                    // already exists) — exit anyway without losing the flow.
                    sparkToast.warning(result?.message || 'Could not save the task as a draft');
                    navigate('/');
                    return;
                }
                refreshUsecases((updatedUsecases) => {
                    const created = (updatedUsecases || []).find(
                        (u) => u.group_name === groupName && u.sub_group_name === subGroupName
                    );
                    if (created) {
                        saveTaskDraft(created._id, 'business-goals');
                        stampWizardStep(created._id, 'business-goals');
                    }
                    exitToProject(groupName);
                });
            },
            () => {
                sparkToast.warning('Could not save the task as a draft');
                navigate('/');
            }
        );
    }, [
        buildUsecaseUpdatePayload,
        createdUsecase,
        createdUsecaseId,
        effectiveStep,
        exitToProject,
        form,
        isDemo,
        navigate,
        projectParam,
        refreshUsecases,
        resumeFormReady,
    ]);

    // In-step "Back" buttons navigate one step backwards; from the first
    // step they exit to the Projects page like the top-left control.
    const handleStepBack = useCallback(() => {
        if (effectiveStepIdx <= 0) {
            handleExitToProjects();
            return;
        }
        goToStep(steps[effectiveStepIdx - 1].key);
    }, [effectiveStepIdx, goToStep, handleExitToProjects, steps]);

    /**
     * Legacy `moveToScreen(id, screen)` contract used by the hosted step
     * screens: step-screen keys map onto wizard steps; anything else
     * (models, settings, ...) exits the wizard to the app-level route.
     */
    const closeReview = useCallback(() => {
        setReviewOpen(false);
        setEditorOpen(false);
    }, []);

    const openDataEditor = useCallback(
        (options = {}) => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (options.tab) next.set('tab', options.tab);
                else next.delete('tab');
                if (options.labelFilter) next.set('labelFilter', options.labelFilter);
                else next.delete('labelFilter');
                return next;
            });
            setEditorOpen(true);
        },
        [setSearchParams]
    );

    const closeDataEditor = useCallback(() => {
        setEditorOpen(false);
        setOverviewRefreshKey((key) => key + 1);
    }, []);

    const hostedMoveToScreen = useCallback(
        (id, screen) => {
            // The task page's Data Editor tab is unchanged. Inside the wizard
            // the editor is the modal over Review Training Plan.
            if (screen === 'data-selection') {
                openDataEditor({ tab: 'train-set' });
                return;
            }
            if (screen === 'train') {
                setReviewOpen(true);
                return;
            }
            const screenToStep = {
                'data-overview': 'data-collection',
                'model-setup': 'model-setup',
            };
            if (screenToStep[screen]) {
                goToStep(screenToStep[screen]);
                return;
            }
            // The demo task has no app-level pages; leaving the wizard means
            // leaving the demo.
            navigate(isDemo ? '/' : `/${screen}?usecaseId=${id}`);
        },
        [goToStep, isDemo, navigate, openDataEditor]
    );

    // Training kicked off — land on the Task Page (Data Overview tab) with
    // the "Your Model will be processed now" modal centered over it (Figma
    // 3001-68567); the flag travels via router state. When the training was
    // started by an auto-train armed in this tab, the user already saw the
    // "Training will start when data is ready" modal at arm time, so the
    // "Training Has Started!" duplicate is skipped.
    //
    // `trainingScheduled`: the wizard finishes right after that "will start"
    // modal, before any training exists. No processing modal now, and the
    // shown-flag is left alone so the task page can still consume it to skip
    // the duplicate when the auto-train actually fires.
    const finishToTaskPage = useCallback(({ trainingScheduled = false } = {}) => {
        // Demo: no task page to land on — back to the projects page, which
        // shows the "You're all set" modal over the choice screen.
        if (isDemo) {
            navigate('/', { state: { [WALKTHROUGH_COMPLETED_STATE_KEY]: true } });
            return;
        }
        removeTaskDraft(createdUsecaseId);
        const usecase = allUsecases.find((u) => u._id === createdUsecaseId);
        const url = usecase ? taskUrlForUsecase(usecase, { objective: usecase._id }) : null;
        if (usecase && url) {
            setCurrentUsecaseId(usecase._id);
            // The "You're all set" modal belongs to the demo only.
            navigate(url, {
                state: {
                    showProcessingModal:
                        !trainingScheduled && !consumeAutoTrainNotifyModalShown(usecase._id),
                },
            });
        } else {
            navigate('/');
        }
    }, [allUsecases, createdUsecaseId, isDemo, navigate, setCurrentUsecaseId]);

    const handleCreateUsecase = useCallback(() => {
        const subGroupName = sanitizeTaskName(form.taskName, { trimEnd: true });
        const groupName = projectParam || DEFAULT_PROJECT_NAME;
        const isClassification = form.taskType === TaskType.classification;
        const isNer = form.taskType === TaskType.NER;

        const { confidence_rejection_enabled: confidenceRejectionEnabled, rejection_label_name: rejectionLabelName } =
            fallbackPayloadFromSelect(form.fallbackLabel, {
                isClassification,
                classificationType: form.classificationType,
            });

        // Demo: the "create"/"update" is a local state change on the demo
        // task — same fields a real create sends, nothing leaves the browser.
        if (isDemo) {
            setDemoUsecase((current) => ({
                ...current,
                usecase_name: subGroupName,
                sub_group_name: subGroupName,
                usecase_goal: form.description || '',
                task_type: form.taskType,
                business_goal_priorities: form.priorityOrder,
                constraints: constraintsToPayload(form.constraints),
                autonomy_level: normalizeAutonomyLevel(form.autonomyLevel),
                ...(isClassification && {
                    is_multilabel: form.classificationType === 'multi',
                    confidence_rejection_enabled: confidenceRejectionEnabled,
                    rejection_label_name: rejectionLabelName,
                    manually_added_labels: form.labels,
                    label_priority: form.labelPriority,
                }),
                ...(isNer && { manually_added_entities: form.labels }),
            }));
            setCreatedUsecaseId(DEMO_USECASE_ID);
            goToStep(stepAfterBusinessGoals);
            return;
        }

        // The task was already created (the user navigated back to this step),
        // so "Next" updates it in place instead of creating a duplicate.
        if (createdUsecaseId) {
            setIsSubmitting(true);
            setSubmitError(null);
            data_from_server_post(
                'update_llm_usecase',
                buildUsecaseUpdatePayload(subGroupName),
                (result) => {
                    if (!result?.success) {
                        setIsSubmitting(false);
                        setSubmitError(result?.message || 'Error updating task');
                        return;
                    }
                    refreshUsecases(() => {
                        setIsSubmitting(false);
                        // Switching to an agentic level on a task whose agent
                        // config has no tools yet: the stepper must not let
                        // the user skip Agent Config into Data Collection.
                        if (isAgentic && !agentConfigHasTools(createdUsecase?.agent_config)) {
                            setMaxReachedStepKey(AGENT_CONFIG_STEP.key);
                        }
                        goToStep(stepAfterBusinessGoals);
                    });
                },
                (error) => {
                    setIsSubmitting(false);
                    setSubmitError(error || 'Error updating task');
                }
            );
            return;
        }

        const task = {
            usecase_name: subGroupName,
            sub_group_name: subGroupName,
            usecase_goal: form.description || '',
            task_type: form.taskType,
            task_languages: ['en'],
            business_goal_priorities: form.priorityOrder,
            constraints: constraintsToPayload(form.constraints),
            autonomy_level: normalizeAutonomyLevel(form.autonomyLevel),
        };
        if (isClassification) {
            task.is_multilabel = form.classificationType === 'multi';
            task.confidence_rejection_enabled = confidenceRejectionEnabled;
            task.rejection_label_name = rejectionLabelName;
            task.manually_added_labels = form.labels;
            task.label_priority = form.labelPriority;
        }
        if (isNer) {
            task.manually_added_entities = form.labels;
        }

        setIsSubmitting(true);
        setSubmitError(null);
        data_from_server_post(
            'create_llm_usecase',
            {
                group_name: groupName,
                client_id: DEFAULT_CLIENT_ID,
                tasks: [task],
            },
            (result) => {
                if (!result?.success) {
                    setIsSubmitting(false);
                    setSubmitError(result?.message || 'A task with this name may already exist');
                    return;
                }
                refreshUsecases((updatedUsecases) => {
                    setIsSubmitting(false);
                    const created = (updatedUsecases || []).find(
                        (u) =>
                            u.group_name === groupName &&
                            u.sub_group_name === subGroupName &&
                            u.usecase_name === subGroupName
                    );
                    if (!created) {
                        sparkToast.error('Task created but could not be loaded — try refreshing.');
                        return;
                    }
                    setCreatedUsecaseId(created._id);
                    setCurrentUsecaseId(created._id);
                    goToStep(stepAfterBusinessGoals, created._id);
                });
            }
        );
    }, [
        buildUsecaseUpdatePayload,
        createdUsecase,
        createdUsecaseId,
        form,
        goToStep,
        isAgentic,
        isDemo,
        projectParam,
        refreshUsecases,
        setCurrentUsecaseId,
        stepAfterBusinessGoals,
    ]);

    // Demo: the hosted pages read the task from GlobalContext, so the whole
    // card (and the runner, whose anchors live inside it) is scoped to the
    // demo task. The sub group mirrors what the real provider derives.
    const demoSubGroup = useMemo(
        () =>
            demoUsecase
                ? {
                      sub_group_name: demoUsecase.sub_group_name?.trim() || demoUsecase.usecase_name,
                      usecases: [demoUsecase],
                  }
                : null,
        [demoUsecase]
    );
    // Demo: `DemoScope` provides the mock task and the wizard api registry.
    // Spark: the real wizard needs the registry too, so step apis
    // (`dataCollection`, `modelSetup`, …) are reachable by the assistant.
    const Scope = isDemo ? DemoScope : assistantOpen ? WizardApiProvider : React.Fragment;
    const scopeProps = isDemo ? { usecase: demoUsecase, onChange: setDemoUsecase } : {};

    // What a script drives on this page — the Business Goals answers (patched
    // into the form the step renders from), the page turns (the step's own
    // Next) and the busy state the page's Next button shows meanwhile. Used
    // by the demo tour (`demoScript.js`, run by `DemoTourRunner`) and by the
    // Spark assistant's `set_form` / `create_usecase` / `go_to_step` actions.
    const patchForm = useCallback((patch) => setForm((prev) => ({ ...prev, ...patch })), []);
    const [demoNextLoading, setDemoNextLoading] = useState(false);
    const wizardApi = useMemo(
        () => ({
            setForm: patchForm,
            createUsecase: handleCreateUsecase,
            goToStep,
            setNextLoading: setDemoNextLoading,
        }),
        [patchForm, handleCreateUsecase, goToStep]
    );
    const demoWizardApi = isDemo ? wizardApi : null;

    // Closing Spark shows the question-mark button that opens it again.
    const showReopenAssistantButton = assistantAvailable && !assistantOpen;

    return (
        <div className="cw-page fs-unmask">
            <div className="cw-page__topbar">
                <button type="button" className="cw-back-button" onClick={handleExitToProjects}>
                    <SparkIcon slug="arrow-left" size="small" />
                    <span>Back</span>
                </button>
                {showReopenAssistantButton && (
                    <Button
                        variant="tertiary"
                        size="medium"
                        type="button"
                        icon={<SparkIcon slug="circle-help" size="medium" />}
                        iconSlug="circle-help"
                        aria-label={REOPEN_ASSISTANT_LABEL}
                        data-testid="reopen-assistant"
                        onClick={handleReopenAssistant}
                    />
                )}
            </div>

            <Scope {...scopeProps}>
                <div className="cw-page__layout" data-assistant-open={assistantOpen ? 'true' : undefined}>
                    {assistantOpen && (
                        <div className="cw-page__assistant-column">
                            <RegisterWizardApi name={WIZARD_API.wizard} api={wizardApi} />
                            <WizardAssistant
                                className="cw-page__assistant"
                                step={effectiveStep}
                                form={form}
                                usecaseId={createdUsecaseId}
                                isSaas={isSaas}
                                user={auth?.user}
                                onClose={handleCloseAssistant}
                            />
                        </div>
                    )}
                    <div className="cw-card">
                        <div className="cw-card__header">
                            <WizardStepper
                                steps={steps}
                                activeStep={effectiveStep}
                                maxReachedStep={createdUsecaseId ? maxReachedStep : 'business-goals'}
                                onStepClick={(stepKey) => goToStep(stepKey)}
                            />
                        </div>
        
                        <div className="cw-card__body spark-native-scrollbar" ref={cardBodyRef}>
                            {effectiveStep === 'business-goals' && (
                                <BusinessGoalsStep
                                    form={form}
                                    onFormChange={(nextForm) => {
                                        if (submitError && nextForm.taskName !== form.taskName) {
                                            setSubmitError(null);
                                        }
                                        setForm(nextForm);
                                    }}
                                    onNext={handleCreateUsecase}
                                    isSubmitting={isSubmitting}
                                    submitError={submitError}
                                    demoLoading={demoNextLoading}
                                />
                            )}
                            {effectiveStep === AGENT_CONFIG_STEP.key && resumeFormReady && (
                                <AgentConfigStep
                                    usecase={createdUsecase}
                                    taskName={taskName}
                                    onBack={handleStepBack}
                                    onNext={() => goToStep('data-collection')}
                                    onSaved={refreshUsecases}
                                />
                            )}
                            {effectiveStep === 'data-collection' && resumeFormReady && (
                                <DataCollectionStep
                                    usecase={createdUsecase}
                                    subGroup={isDemo ? demoSubGroup : currentSubGroup}
                                    taskName={taskName}
                                    form={form}
                                    onFormChange={setForm}
                                    onBack={handleStepBack}
                                    onNext={() => goToStep('model-setup')}
                                    stateCacheRef={dataCollectionCacheRef}
                                    demoLoading={demoNextLoading}
                                />
                            )}
                            {effectiveStep === 'model-setup' && (
                                <ModelSetupStep
                                    activeUsecaseId={createdUsecaseId}
                                    taskName={taskName}
                                    onBack={handleStepBack}
                                    onNext={() => setReviewOpen(true)}
                                    demoLoading={demoNextLoading}
                                />
                            )}
                        </div>
                    </div>
                </div>

            {/* Auto-train scheduled from the Train step: keep the docked
                bottom-right flows card visible while the wizard is open
                (same card the task page shows). */}
            {createdUsecase?.auto_train_pending ? (
                <AutoTrainProgressBanner usecase={createdUsecase} />
            ) : null}

            <ReviewTrainingPlanModal
                open={reviewOpen && Boolean(createdUsecaseId)}
                hidden={editorOpen}
                usecase={createdUsecase}
                refreshKey={overviewRefreshKey}
                moveToScreen={hostedMoveToScreen}
                onClose={closeReview}
                onFinished={finishToTaskPage}
                onOpenDataEditor={openDataEditor}
            />
            <WizardDataEditorModal
                open={editorOpen}
                onClose={closeDataEditor}
                moveToScreen={hostedMoveToScreen}
            />

            {/* The demo tour is started by `useDemoTour` and turns each page
                itself. After Model Setup, Finish & Train opens Review Training
                Plan; Start Training ends the demo on the projects page
                (`finishToTaskPage`). The real wizard mounts no runner: no
                coach mark is shown outside the guided tour, and any state an
                earlier build saved (a stale `create_model` pointer, the
                retired `wizard_*` page guides) is left alone. */}
            {isDemo && (
                <>
                    <RegisterWizardApi name={WIZARD_API.wizard} api={demoWizardApi} />
                    <DemoTourRunner currentPage={effectiveStep} pageOrder={walkthroughPageOrder} />
                </>
            )}
            </Scope>
        </div>
    );
};

export default CreateTaskWizardPage;
